<template>
  <am-menu-anchor :class="classes">
    <am-text-field
      v-bind="$attrs"
      :label="label"
      :disabled="disabled"
      trailing-icon="arrow_drop_down"
      :value="inputValue"
      :errorText="errorText"
      @input="onSearchText"
      @click="onShowMenu"
      @blur="onCloseMenu"
      @keydown.up="onKeyUp"
      @keydown.down="onKeyDown"
      @keydown.enter="onKeyEnter"
      @keydown.space="onKeySpace"
      :readonly="!isOpen"
      :placeholder="placeholder"
      ref="textfield"
    />
    <am-menu
      anchorCorner="BOTTOM_START"
      defaultFocusState="NONE"
      v-model="isOpen"
      @selected="onSelect"
      :style="{ width: width + 'px' }"
      ref="menu"
      :isHoisted="isHoisted"
      :fixed="fixed"
    >
      <am-list>
        <am-list-item
          @mousedown="onMenuSelect"
          v-for="(option, index) in options"
          :key="option.value"
          :selected="`${value}` === `${option.value}`"
          :focused="index === selectedIndex"
        >
          {{ option.label }}
        </am-list-item>
      </am-list>
    </am-menu>
  </am-menu-anchor>
</template>

<script>
import scrollIntoView from "scroll-into-view-if-needed";
import { AmTextField } from "../TextField";
import { AmList, AmListItem } from "../List";
import { AmMenu, AmMenuAnchor } from "../Menu";

export default {
  model: {
    prop: "value",
    event: "model"
  },
  inheritAttrs: false,
  components: {
    AmTextField,
    AmMenuAnchor,
    AmMenu,
    AmList,
    AmListItem
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      required: true
    },
    /**
     * Validation error text
     */
    errorText: {
      type: String,
      default: ""
    },
    /**
     * Sets whether the menu surface has been hoisted to the body so that the offsets are calculated relative to the page and not the anchor.
     */
    isHoisted: {
      type: Boolean,
      default: false
    },
    /**
     * Sets menu surface to fixed position relative to element.
     */
    fixed: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ""
    },
    /**
     * Selected value
     */
    value: {
      type: [String, Number],
      default: ""
    },
    /**
     * Array of objects for select dropdown: Array<{label: String , value: String | Number}>
     */
    options: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isOpen: false,
      searchText: "",
      selectedIndex: -1,
      width: 500
    };
  },
  watch: {
    searchText(value) {
      const search = value.toLowerCase();

      if (!search) {
        this.selectedIndex = -1;
        return;
      }

      this.selectedIndex = this.options.findIndex(({ label }) => label.toLowerCase().indexOf(search) === 0);
    },
    selectedIndex(index) {
      const el = this.$refs.menu.mdcMenu.items[index];

      if (el) {
        scrollIntoView(el, {
          scrollMode: "if-needed",
          block: "center"
        });
      }
    }
  },
  computed: {
    classes() {
      return [
        "am-autocomplete",
        {
          "am-autocomplete--open": this.isOpen
        }
      ];
    },
    inputValue() {
      if (this.isOpen) {
        return this.searchText;
      }

      if (this.valueOption) {
        return this.valueOption.label;
      }

      return "";
    },
    valueOption() {
      return this.options.find(({ value }) => `${value}` === `${this.value}`);
    },
    maxIndex() {
      return this.options.length - 1;
    }
  },
  methods: {
    onShowMenu() {
      this.isOpen = true;
      this.searchText = "";
      this.width = this.$refs.textfield.$el.clientWidth;
    },
    onCloseMenu(event) {
      this.isOpen = false;
      this.$emit("blur", event);
    },
    onSearchText(e) {
      this.searchText = e.target.value;
    },
    onSelect({ index }) {
      const { value } = this.options[index];

      this.selectedIndex = index;
      this.isOpen = false;

      /**
       * Select event
       * @type {String | Number}
       */
      this.$emit("model", value);
    },
    onMenuSelect(e) {
      e.preventDefault();
      return false;
    },
    onKeyEnter(event) {
      if (this.isOpen) {
        event.stopPropagation();
        event.preventDefault();
        if (this.options[this.selectedIndex]) {
          this.onSelect({ index: this.selectedIndex });
        }
      }
    },
    onKeyUp() {
      if (this.isOpen) {
        this.selectedIndex = this.selectedIndex <= 0 ? this.maxIndex : this.selectedIndex - 1;
      }
    },
    onKeySpace(event) {
      if (!this.isOpen) {
        event.stopPropagation();
        event.preventDefault();
        this.onShowMenu();
      }
    },
    onKeyDown() {
      if (this.isOpen) {
        this.selectedIndex = this.selectedIndex >= this.maxIndex ? 0 : this.selectedIndex + 1;
      } else {
        this.onShowMenu();
      }
    }
  }
};
</script>
